import { useController } from "react-hook-form";
import type { Control, FieldValues, Path } from "react-hook-form";
import type { ChangeEvent, ReactElement, ReactNode } from "react";
import type { FormField } from "../../types/niche";
import { maskBRL, maskCPF, maskCpfCnpj, maskPhone } from "../../lib/masks";
import { todayISO } from "../../lib/deadline";

interface Props<T extends FieldValues> {
  field: FormField;
  name: Path<T>;
  control: Control<T>;
  hint?: ReactNode;
}

const MASKS: Partial<Record<FormField["type"], (v: string) => string>> = { cpf: maskCPF, tel: maskPhone, currency: maskBRL, document: maskCpfCnpj };
const HTML_TYPE: Partial<Record<FormField["type"], string>> = { email: "email", date: "date", tel: "tel" };
const NUMERIC = new Set<FormField["type"]>(["cpf", "currency", "tel", "document"]);
const WIDE = new Set<FormField["type"]>(["text", "email", "textarea", "radio"]);
const SECURITY_NOTE = new Set<FormField["type"]>(["email", "cpf", "document"]);
export const SECURITY_MICROCOPY = "🔒 Segurança: O Gepeto nunca solicitará senhas, tokens ou dados de acesso bancário.";

const baseInput =
  "w-full rounded-md border bg-slate-950 px-4 py-3 text-base text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 sm:text-sm [color-scheme:dark]";

function TermsLabel({ label }: { label: string }) {
  const [before, after] = label.split("Termos de Uso");
  if (after === undefined) return <>{label}</>;
  return (
    <>
      {before}
      <a href="/termos" target="_blank" rel="noopener noreferrer" data-testid="terms-link" className="text-emerald-400 underline underline-offset-4 hover:text-emerald-300">
        Termos de Uso
      </a>
      {after}
    </>
  );
}

export function FormFieldControl<T extends FieldValues>({ field, name, control, hint }: Props<T>) {
  const { field: f, fieldState } = useController({ name, control });
  const id = `field-${name}`;
  const errorId = `${id}-error`;
  const invalid = Boolean(fieldState.error);
  const border = invalid ? "border-red-500/70" : "border-slate-800";
  const error = fieldState.error?.message && (
    <p id={errorId} role="alert" data-testid={`error-${name}`} className="mt-2 text-xs text-red-400">{fieldState.error.message}</p>
  );
  const aria = { "aria-invalid": invalid, "aria-describedby": invalid ? errorId : undefined };

  if (field.type === "checkbox") {
    return (
      <div className="sm:col-span-2">
        <label htmlFor={id} className="flex cursor-pointer gap-3 rounded-md border border-slate-800 bg-slate-950/60 p-4 text-sm leading-relaxed text-slate-300">
          <input id={id} type="checkbox" ref={f.ref} name={f.name} checked={f.value === true} onChange={(e) => f.onChange(e.target.checked)} onBlur={f.onBlur} {...aria} data-testid={`input-${name}`} className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-emerald-500" />
          <span><TermsLabel label={field.label} /></span>
        </label>
        {error}
      </div>
    );
  }

  const value = typeof f.value === "string" ? f.value : "";
  const mask = MASKS[field.type];
  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => f.onChange(mask ? mask(e.target.value) : e.target.value);
  const common = { id, name: f.name, value, onChange, onBlur: f.onBlur, ...aria, "data-testid": `input-${name}` };
  const label = (
    <label htmlFor={id} className="mb-2 block text-sm font-medium text-slate-300">
      {field.label}
      {field.required && <span className="ml-1 text-emerald-500" aria-hidden="true">*</span>}
    </label>
  );

  let widget: ReactElement;
  if (field.type === "select") {
    widget = (
      <select {...common} ref={f.ref} className={`${baseInput} ${border} ${value ? "" : "text-slate-500"}`}>
        <option value="" disabled>{field.placeholder ?? "Selecione"}</option>
        {field.options?.map((o) => <option key={o} value={o} className="text-slate-100">{o}</option>)}
      </select>
    );
  } else if (field.type === "textarea") {
    widget = (
      <>
        <textarea {...common} ref={f.ref} rows={4} maxLength={field.maxLength} placeholder={field.placeholder} className={`${baseInput} ${border} resize-y`} />
        {field.maxLength && <p data-testid={`counter-${name}`} className="mt-1 text-right font-mono text-[11px] text-slate-500">{value.length}/{field.maxLength}</p>}
      </>
    );
  } else if (field.type === "radio") {
    widget = (
      <div role="radiogroup" aria-labelledby={`${id}-label`} className="grid gap-3 sm:grid-cols-2">
        {field.options?.map((o, i) => (
          <label key={o} className={`flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3 text-sm transition-colors duration-200 ${value === o ? "border-emerald-500 bg-emerald-500/10 text-slate-100" : "border-slate-800 bg-slate-950 text-slate-300 hover:border-slate-700"}`}>
            <input type="radio" name={f.name} value={o} checked={value === o} ref={i === 0 ? f.ref : undefined} onChange={() => f.onChange(o)} onBlur={f.onBlur} data-testid={`input-${name}-${o}`} className="h-4 w-4 accent-emerald-500" />
            {field.optionLabels?.[o] ?? o}
          </label>
        ))}
      </div>
    );
  } else {
    widget = (
      <div className="relative">
        {field.type === "currency" && <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 font-mono text-sm text-slate-500">R$</span>}
        <input
          {...common}
          ref={f.ref}
          type={HTML_TYPE[field.type] ?? "text"}
          inputMode={NUMERIC.has(field.type) ? "numeric" : undefined}
          autoComplete={field.type === "email" ? "email" : field.type === "tel" ? "tel" : undefined}
          max={field.type === "date" ? todayISO() : undefined}
          maxLength={field.maxLength}
          placeholder={field.placeholder}
          className={`${baseInput} ${border} ${field.type === "currency" ? "pl-11" : ""}`}
        />
      </div>
    );
  }

  return (
    <div className={WIDE.has(field.type) ? "sm:col-span-2" : undefined}>
      {field.type === "radio" ? <p id={`${id}-label`} className="mb-2 block text-sm font-medium text-slate-300">{field.label}</p> : label}
      {widget}
      {hint}
      {error}
      {SECURITY_NOTE.has(field.type) && <p data-testid="security-microcopy" data-field={name} className="mt-2 text-xs leading-relaxed text-slate-500">{SECURITY_MICROCOPY}</p>}
    </div>
  );
}
